/**
 * RFC 4648 Base32 (A–Z, 2–7) encode/decode for TOTP secrets.
 * Padding (`=`) is accepted on decode and omitted on encode (common for otpauth).
 */

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

/** Both cases in one table; `indexOf(…) % 32` folds lowercase onto the same symbol. */
const DECODE_ALPHABET = ALPHABET + ALPHABET.toLowerCase();

export class Base32Error extends Error {
  constructor(message: string) {
    super(message);
    this.name = "Base32Error";
  }
}

/** Encode bytes to unpadded Base32. */
export function encodeBase32(bytes: Uint8Array): string {
  let bits = 0;
  let value = 0;
  let output = "";

  for (const byte of bytes) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      output += ALPHABET.charAt((value >>> bits) & 0x1f);
    }
  }

  if (bits > 0) {
    output += ALPHABET.charAt((value << (5 - bits)) & 0x1f);
  }

  return output;
}

/**
 * Decode Base32 (with or without padding / spaces / case).
 * Throws {@link Base32Error} on invalid input.
 */
export function decodeBase32(input: string): Uint8Array<ArrayBuffer> {
  const cleaned = input.replace(/[\s=-]/g, "");

  let bits = 0;
  let value = 0;
  const bytes: number[] = [];

  for (const char of cleaned) {
    const symbol = DECODE_ALPHABET.indexOf(char);
    if (symbol < 0) {
      throw new Base32Error(`Invalid Base32 character: ${JSON.stringify(char)}`);
    }
    value = (value << 5) | (symbol % 32);
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((value >>> bits) & 0xff);
    }
  }

  return Uint8Array.from(bytes);
}

/** Generate a cryptographically random Base32 secret (default 20 bytes → 160-bit). */
export function generateSecret(byteLength = 20): string {
  if (!Number.isInteger(byteLength) || byteLength < 10 || byteLength > 64) {
    throw new RangeError("byteLength must be an integer between 10 and 64");
  }
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return encodeBase32(bytes);
}
